import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';

export const Menu = RadixMenu.Root;
export const MenuTrigger = RadixMenu.Trigger;

export interface MenuContentProps {
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
  label?: string;
  className?: string;
}

export function MenuContent({ children, align = 'end', label, className }: MenuContentProps) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        align={align}
        sideOffset={6}
        collisionPadding={8}
        aria-label={label}
        className={cn(
          'z-dropdown min-w-[12rem] max-w-[20rem] rounded-lg border border-border-subtle bg-surface-raised p-1 shadow-2',
          className,
        )}
      >
        {children}
      </RadixMenu.Content>
    </RadixMenu.Portal>
  );
}

const itemClass = cn(
  'flex min-h-touch-min cursor-pointer select-none items-center gap-2 rounded-md px-3 text-sm text-primary outline-none md:min-h-control-md',
  'data-[highlighted]:bg-surface-tint data-[disabled]:cursor-not-allowed data-[disabled]:text-disabled',
);

export interface MenuItemProps {
  children: ReactNode;
  icon?: LucideIcon;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  lang?: string;
}

export function MenuItem({ children, icon, onSelect, disabled, destructive, lang }: MenuItemProps) {
  return (
    <RadixMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      lang={lang}
      className={cn(itemClass, destructive && 'text-danger-text')}
    >
      {icon ? <Icon icon={icon} size={18} /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </RadixMenu.Item>
  );
}

export interface MenuRadioItemProps {
  value: string;
  children: ReactNode;
  lang?: string;
}

export const MenuRadioGroup = RadixMenu.RadioGroup;

export function MenuRadioItem({ value, children, lang }: MenuRadioItemProps) {
  return (
    <RadixMenu.RadioItem value={value} lang={lang} className={itemClass}>
      <span className="flex size-4 items-center justify-center">
        <RadixMenu.ItemIndicator>
          <Icon icon={Check} size={16} />
        </RadixMenu.ItemIndicator>
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </RadixMenu.RadioItem>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <RadixMenu.Label className="px-3 py-2 text-xs font-medium text-muted">{children}</RadixMenu.Label>;
}

export function MenuSeparator() {
  return <RadixMenu.Separator className="my-1 h-px bg-border-subtle" />;
}
